import { TaxResidencyInformation } from "../../model/TaxResidencyType";

const TAX_NUMBER_REGEX = /^[a-zA-ZčćžđšČĆŽĐŠ0-9/.\- ]+$/;

const TAX_NUMBER_MIN = 1;
const TAX_NUMBER_MAX = 30;

// US TIN validation (exactly 9 digits)
const US_TIN_REGEX = /^[0-9]+$/;
const US_TIN_LENGTH = 9;

type SetFieldError = (field: string, message: string | undefined) => void;

const asTrimmedString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const validateRequired = (value: unknown): string | undefined => {
    return asTrimmedString(value) ? undefined : "common.validation-error.required";
};

const validateTaxNumberValue = (value: unknown): string | undefined => {
    const trimmedValue = typeof value === "string" ? value.trim() : "";

    if (!trimmedValue) {
        return "common.validation-error.required";
    }

    if (trimmedValue.length < TAX_NUMBER_MIN || trimmedValue.length > TAX_NUMBER_MAX) {
        return "tax-residency-page.american-tax-number-field.validation-error.length";
    }

    if (!TAX_NUMBER_REGEX.test(trimmedValue)) {
        return "common.validation-error.type.letter-number-space-line-dot-dash";
    }

    return undefined;
};

const validateUsTinValue = (value: unknown): string | undefined => {
    const trimmedValue = typeof value === "string" ? value.trim() : "";

    if (!trimmedValue) {
        return "common.validation-error.required";
    }

    if (trimmedValue.length !== US_TIN_LENGTH) {
        return "tax-residency-page.american-tax-number-field.validation-error.length-specific";
    }

    if (!US_TIN_REGEX.test(trimmedValue)) {
        return "common.validation-error.type.number";
    }

    return undefined;
};

const computeFieldError = (field: string, value: unknown): string | undefined => {
    if (field === "porezniBrojSAD") {
        return validateUsTinValue(value);
    }

    if (field.includes("taxNumber")) {
        return validateTaxNumberValue(value);
    }

    if (field.includes("country")) {
        return validateRequired(value);
    }

    return undefined;
};

export const validateField = (
  field: string,
  value: unknown,
  setFieldError: SetFieldError
): boolean => {
    const error = computeFieldError(field, value);
    setFieldError(field, error);
    return error === undefined;
};

export const validateDrzaveOporezivanja = (
  drzaveOporezivanja: TaxResidencyInformation["drzaveOporezivanja"],
  setFieldError: SetFieldError
): boolean => {
    let valid = true;

    for (let i = 0; i < drzaveOporezivanja.length; i++) {
        const entry = drzaveOporezivanja[i];
        const countryCode = entry.country?.oznaka;

        const isCountryValid = validateField(
          `drzaveOporezivanja[${i}].country`,
          countryCode,
          setFieldError
        );

        let isTaxNumberValid = true;
        if (countryCode && countryCode !== "HR") {
            isTaxNumberValid = validateField(
              `drzaveOporezivanja[${i}].taxNumber`,
              entry.taxNumber,
              setFieldError
            );
        }

        if (!isCountryValid || !isTaxNumberValid) {
            valid = false;
        }
    }

    return valid;
};

export const validateForm = (
  values: TaxResidencyInformation,
  setFieldError: SetFieldError
): boolean => {
    let valid = true;

    if (values.porezniObveznikDrzaveKojaNijeSAD) {
        if (!validateDrzaveOporezivanja(values.drzaveOporezivanja ?? [], setFieldError)) {
            valid = false;
        }
    }

    if (values.posjedujePorezniBrojSAD) {
        const ok = validateField("porezniBrojSAD", values.porezniBrojSAD, setFieldError);
        if (!ok) {
            valid = false;
        }
    }

    return valid;
};

export const isDrzaveOporezivanjaValid = (
  drzaveOporezivanja: TaxResidencyInformation["drzaveOporezivanja"]
): boolean => {
    for (const entry of drzaveOporezivanja) {
        const countryCode = entry.country?.oznaka?.trim() ?? "";
        if (!countryCode) return false;

        if (countryCode === "HR") {
            continue;
        }

        const taxNumberError = validateTaxNumberValue(entry.taxNumber);
        if (taxNumberError) return false;
    }

    return true;
};
